import { useEffect, useCallback, useMemo } from "react";

import { isGridOptionSiggestion } from "@/components/suggestions/list/utils";
import { keyMatcher } from "@/utils/keyMatcher";

import type { Suggestion } from "@/components/suggestions/types";
import type { EditorModel } from "@/models/EditorModel";

type Props = {
  editor: EditorModel;
  listId: string | null;
  list: Suggestion[];
  onActiveIndexChange: (listId: string | null, index: number, fromKeyboard?: boolean) => void;
  disabled?: boolean;
};

export function useSuggestionsNavigation({
  editor,
  listId,
  list,
  onActiveIndexChange: onActiveIndexChangeProp,
  disabled,
}: Props) {
  // All navigation here is keyboard-driven, so flag the change as such — this is
  // what lets the active item scroll into view (hover changes are left unscrolled).
  const onActiveIndexChange = useCallback(
    (id: string | null, index: number) => {
      onActiveIndexChangeProp(id, index, true);
    },
    [onActiveIndexChangeProp],
  );

  const { symbolIndices, symbolsMatrix } = useMemo(() => {
    const indices = getGridIndices(list);
    const matrix = getGridMatrix(indices);

    return {
      symbolIndices: indices,
      symbolsMatrix: matrix,
    };
  }, [list]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (disabled) return;
      const { activeIndex } = editor.suggestions;

      const isHorizontalNavDisabled =
        symbolIndices.length === 0 || symbolIndices.length === list.length;

      if (keyMatcher("ArrowDown", e)) {
        stopEvent(e);

        const position = getSymbolGridPosition(activeIndex, symbolIndices);

        if (!position || isHorizontalNavDisabled) {
          const nextIndex = nextEnabledIndex(activeIndex, list, 1);
          onActiveIndexChange(listId, nextIndex);
          return;
        }

        const { row, col } = position;
        const currentRow = symbolsMatrix[row];
        const nextRow = symbolsMatrix[row + 1];

        if (nextRow) {
          onActiveIndexChange(listId, nextRow[col] ?? nextRow[nextRow.length - 1]);
          return;
        }

        const afterIndex = currentRow[currentRow.length - 1] + 1;
        onActiveIndexChange(listId, list[afterIndex] ? afterIndex : 0);
        return;
      }

      if (keyMatcher("ArrowUp", e)) {
        stopEvent(e);

        const position = getSymbolGridPosition(activeIndex, symbolIndices);
        const nextIndex = nextEnabledIndex(activeIndex, list, -1);

        if (isHorizontalNavDisabled) {
          onActiveIndexChange(listId, nextIndex);
          return;
        }

        if (!position) {
          const nextPosition = getSymbolGridPosition(nextIndex, symbolIndices);

          onActiveIndexChange(
            listId,
            nextPosition ? symbolsMatrix[nextPosition.row][0] : nextIndex,
          );
          return;
        }

        const { row, col } = position;

        if (row === 0) {
          const prevIndex = symbolsMatrix[0][0] - 1;
          onActiveIndexChange(listId, prevIndex === -1 ? list.length - 1 : prevIndex);
          return;
        }

        onActiveIndexChange(listId, symbolsMatrix[row - 1][col]);
        return;
      }

      if (keyMatcher("ArrowLeft", e)) {
        const currentItem = list[activeIndex];

        if (!isHorizontalNavDisabled && isGridOptionSiggestion(currentItem)) {
          const nextIndex = nextEnabledIndex(activeIndex, list, -1);

          stopEvent(e);
          onActiveIndexChange(listId, nextIndex);
        }

        return;
      }

      if (keyMatcher("ArrowRight", e)) {
        const currentItem = list[activeIndex];

        if (!isHorizontalNavDisabled && isGridOptionSiggestion(currentItem)) {
          const nextIndex = nextEnabledIndex(activeIndex, list, 1);

          stopEvent(e);
          onActiveIndexChange(listId, nextIndex);
        }
      }
    },
    [editor, listId, list, onActiveIndexChange, symbolIndices, symbolsMatrix, disabled],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [handleKeyDown]);
}

const SYMBOLS_PER_ROW = 4;

export function getInitialActiveIndex(list: Suggestion[], initialIndex?: number) {
  if (typeof initialIndex === "number") {
    return initialIndex;
  }

  const [first] = list;

  if (isGridOptionSiggestion(first)) {
    const index = list.findIndex((item) => {
      if (item.type === "multi") {
        return true;
      }

      return !isGridOptionSiggestion(item);
    });

    if (index === -1) return 0;

    return index < list.length ? index : 0;
  }

  return 0;
}

function stopEvent(e: KeyboardEvent) {
  e.preventDefault();
  e.stopPropagation();
}

function cycleIndex(current: number, max: number, increment: number): number {
  const next = current + increment;

  if (next > max - 1) {
    return 0;
  }

  if (next < 0) {
    return max - 1;
  }

  return next;
}

function nextEnabledIndex(current: number, list: Suggestion[], increment: number): number {
  let index = current;

  for (let step = 0; step < list.length; step += 1) {
    index = cycleIndex(index, list.length, increment);

    if (!list[index]?.disabled) {
      return index;
    }
  }

  return current;
}

function getGridIndices(list: Suggestion[]) {
  return list
    .map((suggestion, index) => {
      if (isGridOptionSiggestion(suggestion)) {
        return index;
      }

      return -1;
    })
    .filter((index) => index !== -1);
}

function getSymbolGridPosition(index: number, symbolIndices: number[]) {
  const symbolPosition = symbolIndices.indexOf(index);

  if (symbolPosition === -1) return null;

  return {
    row: Math.floor(symbolPosition / SYMBOLS_PER_ROW),
    col: symbolPosition % SYMBOLS_PER_ROW,
  };
}

function getGridMatrix(symbolIndices: number[]) {
  const matrix: number[][] = [];

  for (let i = 0; i < symbolIndices.length; i += SYMBOLS_PER_ROW) {
    matrix.push(symbolIndices.slice(i, i + SYMBOLS_PER_ROW));
  }

  return matrix;
}
